import React, { useEffect, useMemo, useState } from 'react';
import { fuzzySearch } from '../../utils/fuzzySearch';
import { nombreOperativo } from '../../utils/productName';
import styles from './styles/StockModals.module.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface Local {
    locales_id: number;
    nombre_local: string;
}

interface Producto {
    idProductos: number;
    NombresProductos: string;
    NombreEtiqueta?: string | null;
    es_promocion?: boolean | number;
}

interface AjusteStockModalProps {
    locales: Local[];
    usuario: string;
    onClose: () => void;
    /** Se dispara despues de registrar el movimiento, para refrescar la tabla. */
    onSaved: () => void;
}

/** Motivos de salida. Una entrada casi siempre es una compra o una correccion. */
const MOTIVOS_SALIDA = [
    'Venta Manual',
    'Avería/Desperdicio',
    'Consumo Interno',
    'Ajuste Inventario',
    'Otro',
];

const hoyISO = () => new Date().toISOString().split('T')[0];

/**
 * Entrada / Salida de stock.
 *
 * Es el mismo movimiento que registra la pantalla de Stock del panel anterior
 * (POST /admin/stock/movimiento), pero servido como modal dentro de Articulos
 * para que el operador no tenga que cambiar de pantalla para corregir una
 * existencia que acaba de ver mal.
 */
const AjusteStockModal: React.FC<AjusteStockModalProps> = ({
    locales, usuario, onClose, onSaved,
}) => {
    const [productos, setProductos] = useState<Producto[]>([]);

    const [tipo, setTipo] = useState<'entrada' | 'salida'>('entrada');
    const [busqueda, setBusqueda] = useState('');
    const [idProducto, setIdProducto] = useState('');
    const [cantidad, setCantidad] = useState('');
    const [fecha, setFecha] = useState(hoyISO);
    const [motivo, setMotivo] = useState('');
    const [idLocal, setIdLocal] = useState<number | ''>(
        locales.length === 1 ? locales[0].locales_id : '',
    );

    const [sugerenciasAbiertas, setSugerenciasAbiertas] = useState(false);
    const [guardando, setGuardando] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [ok, setOk] = useState<string | null>(null);

    // Las promociones no tienen stock propio: su stock es el de sus componentes.
    useEffect(() => {
        fetch(`${API}/productos`)
            .then(res => res.json())
            .then(data => {
                if (!Array.isArray(data)) return;
                setProductos(data.filter((p: Producto) => Number(p.es_promocion) !== 1));
            })
            .catch(() => setError('No se pudo cargar el catálogo de productos.'));
    }, []);

    // Si solo hay un local, no se pide elegirlo: ya viene resuelto.
    useEffect(() => {
        if (locales.length === 1) setIdLocal(locales[0].locales_id);
    }, [locales]);

    const coincidencias = useMemo(
        () => fuzzySearch(productos, busqueda, {
            keys: ['NombreEtiqueta', 'NombresProductos'],
            threshold: 0.35,
        }).slice(0, 8),
        [productos, busqueda],
    );

    const elegirTipo = (nuevo: 'entrada' | 'salida') => {
        setTipo(nuevo);
        // Una salida siempre necesita motivo; el mas comun es el ajuste.
        setMotivo(nuevo === 'salida' ? 'Ajuste Inventario' : '');
    };

    const guardar = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setOk(null);

        if (!idProducto) {
            setError('Elegí un producto de la lista.');
            return;
        }
        const cant = Number(cantidad);
        if (!Number.isFinite(cant) || cant <= 0) {
            setError('La cantidad tiene que ser mayor a 0.');
            return;
        }
        if (!idLocal) {
            setError('Elegí la sucursal del movimiento.');
            return;
        }
        if (tipo === 'salida' && !motivo) {
            setError('Elegí el motivo de la salida.');
            return;
        }

        setGuardando(true);
        try {
            const res = await fetch(`${API}/admin/stock/movimiento`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id_producto: idProducto,
                    tipo,
                    cantidad: cant,
                    motivo: motivo || (tipo === 'entrada' ? 'Entrada Manual' : 'Salida Manual'),
                    usuario,
                    fecha,
                    id_local: idLocal,
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setError(data.error || 'No se pudo registrar el movimiento.');
                return;
            }

            setOk(tipo === 'entrada'
                ? `Entrada de ${cant} unidad${cant === 1 ? '' : 'es'} registrada.`
                : `Salida de ${cant} unidad${cant === 1 ? '' : 'es'} registrada.`);
            // El formulario queda listo para el movimiento siguiente: cargar
            // varios ajustes seguidos es el caso normal.
            setBusqueda('');
            setIdProducto('');
            setCantidad('');
            onSaved();
        } catch {
            setError('Error de conexión.');
        } finally {
            setGuardando(false);
        }
    };

    return (
        <div
            className={styles.overlay}
            role="presentation"
            onClick={e => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className={styles.modal} role="dialog" aria-modal="true" aria-label="Ajuste de stock">
                <div className={styles.modalHead}>
                    <div>
                        <h3>Entrada / Salida de Stock</h3>
                        <p>Registra un movimiento manual sobre las existencias.</p>
                    </div>
                    <button type="button" className={styles.modalClose} onClick={onClose} aria-label="Cerrar">
                        ×
                    </button>
                </div>

                <form onSubmit={guardar}>
                    <div className={styles.modalBody}>
                        <div className={styles.tipoSwitch} role="group" aria-label="Tipo de movimiento">
                            <button
                                type="button"
                                className={`${styles.tipoBtn} ${tipo === 'entrada' ? styles.tipoBtnEntrada : ''}`}
                                aria-pressed={tipo === 'entrada'}
                                onClick={() => elegirTipo('entrada')}
                            >
                                Entrada (+)
                            </button>
                            <button
                                type="button"
                                className={`${styles.tipoBtn} ${tipo === 'salida' ? styles.tipoBtnSalida : ''}`}
                                aria-pressed={tipo === 'salida'}
                                onClick={() => elegirTipo('salida')}
                            >
                                Salida (−)
                            </button>
                        </div>

                        <label className={`${styles.field} ${styles.fieldSearch}`}>
                            <span>Producto</span>
                            <input
                                type="text"
                                value={busqueda}
                                autoComplete="off"
                                placeholder="Buscar producto…"
                                onChange={e => {
                                    setBusqueda(e.target.value);
                                    setSugerenciasAbiertas(true);
                                    // Cambiar el texto invalida la eleccion previa: sin
                                    // esto se podria enviar un producto que ya no es el
                                    // que se lee en el campo.
                                    setIdProducto('');
                                }}
                                onFocus={() => setSugerenciasAbiertas(true)}
                            />
                            {sugerenciasAbiertas && busqueda && !idProducto && coincidencias.length > 0 && (
                                <ul className={styles.suggestions}>
                                    {coincidencias.map(p => (
                                        <li key={p.idProductos}>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setBusqueda(nombreOperativo(p));
                                                    setIdProducto(String(p.idProductos));
                                                    setSugerenciasAbiertas(false);
                                                }}
                                            >
                                                {nombreOperativo(p)}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </label>

                        <div className={styles.fieldRow}>
                            <label className={styles.field}>
                                <span>Cantidad</span>
                                <input
                                    type="number"
                                    min="1"
                                    value={cantidad}
                                    onChange={e => setCantidad(e.target.value)}
                                    required
                                />
                            </label>

                            <label className={styles.field}>
                                <span>Fecha</span>
                                <input
                                    type="date"
                                    value={fecha}
                                    onChange={e => setFecha(e.target.value)}
                                    required
                                />
                            </label>
                        </div>

                        <div className={styles.fieldRow}>
                            {locales.length > 1 && (
                                <label className={styles.field}>
                                    <span>Sucursal</span>
                                    <select
                                        value={idLocal}
                                        onChange={e => setIdLocal(Number(e.target.value))}
                                        required
                                    >
                                        <option value="">Seleccionar…</option>
                                        {locales.map(l => (
                                            <option key={l.locales_id} value={l.locales_id}>
                                                {l.nombre_local}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            )}

                            {tipo === 'salida' && (
                                <label className={styles.field}>
                                    <span>Motivo</span>
                                    <select
                                        value={motivo}
                                        onChange={e => setMotivo(e.target.value)}
                                        required
                                    >
                                        <option value="">Seleccionar…</option>
                                        {MOTIVOS_SALIDA.map(m => (
                                            <option key={m} value={m}>{m}</option>
                                        ))}
                                    </select>
                                </label>
                            )}
                        </div>

                        {error && <p className={styles.errorMsg}>{error}</p>}
                        {ok && <p className={styles.okMsg}>{ok}</p>}
                    </div>

                    <div className={styles.modalFoot}>
                        <button type="button" className={styles.ghostBtn} onClick={onClose}>
                            Cerrar
                        </button>
                        <button type="submit" className={styles.primaryBtn} disabled={guardando}>
                            {guardando
                                ? 'Registrando…'
                                : tipo === 'entrada' ? 'Registrar entrada' : 'Registrar salida'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AjusteStockModal;
